import { z } from 'zod';
import type { CalendarItemDto } from './schemas-planning';
import './schemas'; // polskie komunikaty zod

/**
 * „Mój kalendarz” (docs/22): miesięczny kalendarz pracownika — zadania zaplanowane dla niego, nieobecności, wydarzenia firmowe
 * i własne wpisy („Moje zadanie”: np. wizyta u klienta). Wpis jest zadaniem bez branży, czynności i protokołu, przypisanym do
 * autora; trafia na telefon do „Moich zadań” i zamyka się sam następnego dnia rano. Inni widzą tylko „zajęty”.
 */

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Godzina w formacie GG:MM');

export const ownTaskSchema = z
  .object({
    title: z.string().trim().min(2, 'Podaj tytuł').max(200),
    dateFrom: day,
    dateTo: day,
    /** null — cały dzień */
    timeFrom: time.nullable().default(null),
    timeTo: time.nullable().default(null),
    /** klient (kartoteka) albo sprawa — opcjonalnie; sprawa ustala też klienta i adres */
    partyId: z.string().uuid().nullable().default(null),
    caseId: z.string().uuid().nullable().default(null),
    place: z.string().trim().max(300).default(''),
    note: z.string().trim().max(4000).default(''),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['dateTo'] })
  .refine((v) => (v.timeFrom === null) === (v.timeTo === null), { message: 'Podaj obie godziny albo zaznacz „cały dzień”', path: ['timeTo'] })
  .refine((v) => v.timeFrom === null || v.timeTo === null || v.dateFrom !== v.dateTo || v.timeTo > v.timeFrom, { message: 'Godzina „do” musi być późniejsza niż „od”', path: ['timeTo'] });
export type OwnTaskInput = z.infer<typeof ownTaskSchema>;

export interface OwnTaskDto {
  id: string;
  number: string;
  title: string;
  dateFrom: string;
  dateTo: string;
  timeFrom: string | null;
  timeTo: string | null;
  partyId: string | null;
  partyName: string | null;
  /** pierwszy telefon klienta (aplikacja: „Zadzwoń”) */
  partyPhone: string | null;
  caseId: string | null;
  caseNumber: string | null;
  place: string;
  note: string;
  status: string;
}

/** Wyszukiwanie sprawy / klienta do wpisu w aplikacji (te same uprawnienia co wyszukiwarka CRM; bez nich — puste listy). */
export interface MyCalendarSearchDto {
  cases: { id: string; number: string; title: string; clientName: string }[];
  parties: { id: string; displayName: string; city: string; phone: string | null }[];
}

export interface MyCalendarDto {
  from: string;
  to: string;
  /** dni kalendarza (zadania, nieobecności, wydarzenia, własne wpisy) — jak w kalendarzu firmy, dla zalogowanej osoby */
  items: (CalendarItemDto & { own: boolean })[];
  /** własne wpisy z okresu — do okna podglądu / zmiany */
  own: OwnTaskDto[];
}
